import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import { mdiCalendarClockOutline } from "@mdi/js"
import { buildScheduleEventCommunicationTagPresentation } from "../../../../../../../../../../../../../_shared/_logic/communication-presentation"

import {
  buildAppointmentTimeTagPresentation,
  type AppointmentTimeTagInput,
} from "../../../../../../../../../../../../../_shared/_logic/appointment-time-tags"
import { eventTypeLabels } from "../../../../../../../../../../../../../_shared/_logic/form-state"
import {
  buildRichTooltip,
  RICH_TOOLTIP_CLASS,
  tooltipField,
  tooltipText,
  tooltipTitle,
} from "../../../../../../../../../../../../../_shared/_logic/rich-tooltips"
import type { BoardEvent } from "../../../../../../../../../../_logic/calendar-types"

type BoardWorkOrderRef = NonNullable<BoardEvent["workOrder"]>

export type CalendarAppointmentTagPresentation = {
  status: StatusDefinition<ScheduleEventStatus | string>
  subLabel: string | null
  tooltip: string
  tooltipClassName: string
}

export type CalendarEventCardTags = {
  title: string
  addressLabel: string | null
  appointment: CalendarAppointmentTagPresentation
}

export type CalendarBlockerTimeTagPresentation = {
  label: string
  subLabel: string | null
  color: StatusDefinition["color"]
  icon: string
  tooltip: string
  tooltipClassName: string
}

export type CalendarBlockerCardTags = {
  time: CalendarBlockerTimeTagPresentation
  detailLines: string[]
}

const clean = (value: unknown) => `${value ?? ""}`.trim()

const blockerColors: Partial<Record<ScheduleEventType | string, StatusDefinition["color"]>> = {
  ABSENCE: "amber",
  INTERNAL_BLOCK: "#64748b",
}

const sourceLabels: Record<string, string> = {
  CALENDAR_SYNC: "Kalender",
  MANUAL: "Manuell",
  BOT_APPOINTMENT: "Bot",
}

const blockerTypeLabel = (eventType?: string | null) =>
  eventTypeLabels[eventType as ScheduleEventType] ?? "Blocker"

const eventSourceLabel = (event: ScheduleEvent) => {
  const sourceLabel = clean((event as { source_label?: unknown }).source_label)

  if (sourceLabel) return sourceLabel

  const source = clean((event as { source?: unknown }).source).toUpperCase()

  return sourceLabels[source] ?? source
}

const uniqueDetailLines = (values: Array<unknown>, excluded: Set<string>) => {
  const seen = new Set<string>()
  const lines: string[] = []
  for (const value of values) {
    const parts = clean(value)
      .split(/\r?\n/)
      .map((part) => part.trim())
      .filter(Boolean)
    for (const part of parts) {
      const normalized = part.toLowerCase()

      if (excluded.has(normalized) || seen.has(normalized)) continue

      seen.add(normalized)
      lines.push(part)
    }
  }

  return lines
}

const withWorkOrderDuration = (
  event: ScheduleEvent,
  workOrder?: BoardWorkOrderRef | null,
): AppointmentTimeTagInput => {
  const eventDuration = event as {
    estimated_duration_min?: number | string | null
    duration_minutes?: number | string | null
  }

  return {
    ...event,
    estimated_duration_min:
      eventDuration.estimated_duration_min ?? workOrder?.estimated_duration_min ?? null,
    duration_minutes: eventDuration.duration_minutes ?? null,
  }
}

const buildAppointmentTag = ({
  item,
  employeeLabel,
  workOrder,
}: {
  item: BoardEvent
  employeeLabel?: string | null
  workOrder?: BoardWorkOrderRef | null
}): CalendarAppointmentTagPresentation => {
  const timeTag = buildAppointmentTimeTagPresentation(
    withWorkOrderDuration(item.event, workOrder),
    {
      mode: "calendar",
    },
  )
  const communication = buildScheduleEventCommunicationTagPresentation({
    status: item.event.status,
    event: item.event,
  })
  const baseStatus = communication.definition
  const resolvedEmployeeLabel =
    clean(employeeLabel) || clean((item.event as { employee_name?: unknown }).employee_name)

  return {
    status: {
      value: baseStatus?.value ?? item.event.status ?? "APPOINTMENT",
      label: communication.label,
      color: baseStatus?.color ?? "blue",
      icon: baseStatus?.icon ?? mdiCalendarClockOutline,
      description: baseStatus?.description ?? item.statusLabel,
    },
    subLabel: null,
    tooltip:
      communication.tooltip +
      buildRichTooltip([
        tooltipField("Kalenderzeit", timeTag.scheduledPeriodLabel),
        tooltipField("Mitarbeiter", resolvedEmployeeLabel),
        timeTag.isFlex ? tooltipField("Zeitfenster", timeTag.timeWindowLabel) : null,
        timeTag.isFlex ? tooltipField("Geplante Dauer", timeTag.durationLabel) : null,
        timeTag.tooltipDescription ? tooltipText(timeTag.tooltipDescription) : null,
      ]),
    tooltipClassName: RICH_TOOLTIP_CLASS,
  }
}

const appointmentCardTitle = (item: BoardEvent) => {
  const title = clean(item.title) || "Termin"
  const orderNumber = clean(item.workOrder?.order_number)

  return orderNumber ? `${title} (#${orderNumber})` : title
}

const appointmentCardAddress = (item: BoardEvent) => {
  const street = [item.property?.street, item.property?.house_number]
    .map(clean)
    .filter(Boolean)
    .join(" ")
  const city = clean(item.property?.city)
  const projectedAddress = [street, city].filter(Boolean).join(", ")

  return projectedAddress || clean(item.event.location_text) || clean(item.addressLabel) || null
}

export const buildCalendarEventCardTags = ({
  item,
  employeeLabel,
}: {
  item: BoardEvent
  employeeLabel?: string | null
}): CalendarEventCardTags | null => {
  if (item.event.event_type !== "APPOINTMENT") return null

  const workOrder = item.workOrder ?? null
  const appointment = buildAppointmentTag({ item, employeeLabel, workOrder })

  return {
    title: appointmentCardTitle(item),
    addressLabel: appointmentCardAddress(item),
    appointment,
  }
}

export const buildCalendarBlockerCardTags = ({
  item,
}: {
  item: BoardEvent
  employeeLabel?: string | null
}): CalendarBlockerCardTags | null => {
  if (item.event.event_type === "APPOINTMENT") return null

  const timeTag = buildAppointmentTimeTagPresentation(item.event, { mode: "calendar" })
  const eventTypeLabel = blockerTypeLabel(item.event.event_type)
  const sourceLabel = eventSourceLabel(item.event)
  const detailLines = uniqueDetailLines(
    [
      item.subtitle,
      item.event.description,
      item.event.location_text,
      item.addressLabel,
      item.event.customer_note,
    ],
    new Set(
      [item.title, timeTag.label, eventTypeLabel]
        .map((value) => clean(value).toLowerCase())
        .filter(Boolean),
    ),
  )

  return {
    time: {
      label: eventTypeLabel,
      subLabel: null,
      color: blockerColors[item.event.event_type] ?? "#64748b",
      icon: mdiCalendarClockOutline,
      tooltip: buildRichTooltip([
        tooltipTitle(eventTypeLabel),
        tooltipField("Kalenderzeit", timeTag.scheduledPeriodLabel),
        tooltipField("Dauer", timeTag.durationLabel),
        tooltipField("Status", item.statusLabel),
        tooltipField("Quelle", sourceLabel),
      ]),
      tooltipClassName: RICH_TOOLTIP_CLASS,
    },
    detailLines,
  }
}
